// ============================================================================
// POLYGON.IO SERVICE  (via backend proxy — key never leaves the server)
//
// All requests go to /api/market/polygon?p=<path>&<params>
// The backend injects the real API key before forwarding to Polygon.
// ============================================================================

import { API_BASE } from './apiConfig';
import { getAuthHeaders } from './backendApi';

const PROXY = `${API_BASE}/market/polygon`;

// In-memory cache
const cache = new Map();
const CACHE_TTL = {
  quote:    15 * 1000,       // 15s
  snapshot: 60 * 1000,       // 1 min
  history:  5 * 60 * 1000,   // 5 min
  options:  60 * 1000,       // 1 min
  search:   5 * 60 * 1000,   // 5 min
};

// Build a proxy URL: path goes in ?p=, any existing query params are kept separate
function buildUrl(path) {
  const [pathPart, queryPart] = path.split('?');
  let url = `${PROXY}?p=${encodeURIComponent(pathPart)}`;
  if (queryPart) url += `&${queryPart}`;
  return url;
}

async function polygonFetch(path, ttl = CACHE_TTL.quote, { force = false } = {}) {
  const cacheKey = `poly:${path}`;
  const cached = cache.get(cacheKey);
  if (!force && cached && Date.now() - cached.ts < ttl) return cached.data;

  const res = await fetch(buildUrl(path), { headers: getAuthHeaders() });
  if (res.status === 429) throw new Error('Polygon rate limited');
  if (!res.ok) throw new Error(`Polygon HTTP ${res.status}`);

  const data = await res.json();
  cache.set(cacheKey, { data, ts: Date.now() });

  if (cache.size > 500) {
    cache.delete(cache.keys().next().value);
  }

  return data;
}

// ============================================================================
// QUOTES — snapshot (last trade price + real day-over-day change)
// Falls back to /prev if snapshot is unavailable.
// ============================================================================
export async function getQuote(ticker, { force = false } = {}) {
  // Try snapshot first — gives last trade price and correct daily change %
  try {
    const snap = await polygonFetch(
      `/v2/snapshot/locale/us/markets/stocks/tickers/${ticker}`,
      CACHE_TTL.quote,
      { force }
    );
    const t = snap?.ticker;
    if (t) {
      // Prefer last trade price; fall back to current-session close, then prev-day close
      const price = t.lastTrade?.p || t.day?.c || t.prevDay?.c;
      if (price && price > 0) {
        const prevClose = t.prevDay?.c || price;
        return {
          ticker,
          price,
          // Only use TODAY's session values — never fall back to prevDay for open/vwap.
          // prevDay.o is yesterday's open, which would make intraday changePct calculations
          // include the overnight gap and give a completely wrong directional signal.
          open:      t.day?.o > 0 ? t.day.o : 0,
          high:      t.day?.h > 0 ? t.day.h : 0,
          low:       t.day?.l > 0 ? t.day.l : 0,
          prevClose,
          volume:    t.day?.v      || t.prevDay?.v || 0,
          avgVolume: 0,
          vwap:      t.day?.vw > 0 ? t.day.vw : null,
          change:    +(price - prevClose).toFixed(4),
          changePct: prevClose ? +((price - prevClose) / prevClose * 100).toFixed(4) : 0,
          transactions: t.day?.o ? t.lastTrade?.s : t.prevDay?.n,
        };
      }
    }
  } catch { /* fall through to /prev */ }

  // Fallback: previous day close
  const data = await polygonFetch(`/v2/aggs/ticker/${ticker}/prev?adjusted=true`, CACHE_TTL.quote, { force });
  const r = data?.results?.[0];
  if (!r) throw new Error(`No quote for ${ticker}`);
  const price = r.c;
  if (!price || price <= 0) throw new Error(`Zero price for ${ticker}`);

  const prevClose = r.o ?? r.c;
  return {
    ticker: data.ticker || ticker,
    price,
    open: r.o, high: r.h, low: r.l,
    prevClose,
    volume: r.v || 0, avgVolume: 0, vwap: r.vw,
    change:    +(price - prevClose).toFixed(4),
    changePct: prevClose ? +((price - prevClose) / prevClose * 100).toFixed(4) : 0,
    transactions: r.n,
  };
}

// Batch quotes — uses Polygon's multi-ticker snapshot endpoint (1 call for up to 250 tickers).
// Chunks into 250-ticker batches automatically. Falls back to individual calls for any
// tickers that come back empty (e.g. delisted or missing from snapshot).
export async function getBatchQuotes(tickers) {
  if (tickers.length === 0) return {};
  const results = {};
  const CHUNK = 250;

  for (let i = 0; i < tickers.length; i += CHUNK) {
    const chunk = tickers.slice(i, i + CHUNK);
    try {
      const data = await polygonFetch(
        `/v2/snapshot/locale/us/markets/stocks/tickers?tickers=${chunk.join(',')}`,
        CACHE_TTL.snapshot
      );
      for (const t of (data?.tickers || [])) {
        const price = t.lastTrade?.p || t.day?.c || t.prevDay?.c;
        if (!price || price <= 0) continue;
        const prevClose = t.prevDay?.c || price;
        results[t.ticker] = {
          ticker:    t.ticker,
          price,
          open:      t.day?.o > 0 ? t.day.o : 0,
          high:      t.day?.h > 0 ? t.day.h : 0,
          low:       t.day?.l > 0 ? t.day.l : 0,
          prevClose,
          volume:    t.day?.v      || t.prevDay?.v || 0,
          avgVolume: 0,
          vwap:      t.day?.vw > 0 ? t.day.vw : null,
          change:    +(price - prevClose).toFixed(4),
          changePct: prevClose ? +((price - prevClose) / prevClose * 100).toFixed(4) : 0,
        };
      }
    } catch { /* fall through to individual fallback below */ }
  }

  // Individual fallback for any tickers the batch missed
  const missing = tickers.filter(t => !results[t]);
  if (missing.length > 0) {
    await Promise.all(missing.map(async (t) => {
      try { results[t] = await getQuote(t); } catch { /* skip */ }
    }));
  }

  return results;
}

// ============================================================================
// HISTORICAL — daily OHLCV aggregates
// ============================================================================
export async function getHistory(ticker, days = 90) {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - days);
  const from = start.toISOString().split('T')[0];
  const to   = end.toISOString().split('T')[0];

  const data = await polygonFetch(
    `/v2/aggs/ticker/${ticker}/range/1/day/${from}/${to}?adjusted=true&sort=asc&limit=500`,
    CACHE_TTL.history
  );

  const results = data?.results;
  if (!results || results.length === 0) return [];

  return results.map(r => ({
    date:   new Date(r.t).toISOString().split('T')[0],
    open:   r.o,
    high:   r.h,
    low:    r.l,
    close:  r.c,
    volume: r.v,
    vwap:   r.vw,
  }));
}

// ============================================================================
// INTRADAY — 5-minute aggregates
// ============================================================================
// Fetch up to 2 days of 5-min bars so the client can slice any window (market hours, 6H, 12H, 24H).
// Returns raw timestamps — the component formats them in the correct exchange timezone.
export async function getIntraday(ticker, interval = 5) {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 2);
  const from = start.toISOString().split('T')[0];
  const to   = end.toISOString().split('T')[0];

  const data = await polygonFetch(
    `/v2/aggs/ticker/${ticker}/range/${interval}/minute/${from}/${to}?adjusted=true&sort=asc&limit=1000`,
    CACHE_TTL.quote
  );

  const results = data?.results;
  if (!results || results.length === 0) return [];

  return results.map(r => ({
    timestamp: r.t,   // UTC ms — client slices and formats
    price:     r.c,
    open:      r.o,
    high:      r.h,
    low:       r.l,
    volume:    r.v || 0,
    vwap:      r.vw,
  }));
}

// ============================================================================
// OPTIONS — chain snapshot (Greeks, IV, bid/ask, OI)
// ============================================================================
export async function getOptionsChain(underlyingTicker) {
  const data = await polygonFetch(
    `/v3/snapshot/options/${underlyingTicker}?limit=250`,
    CACHE_TTL.options
  );

  const results = data?.results;
  if (!results || results.length === 0) return [];

  return results.map(r => {
    const d  = r.details || {};
    const g  = r.greeks  || {};
    const day = r.day    || {};
    const lq = r.last_quote || {};
    return {
      symbol:             d.ticker,
      underlying:         r.underlying_asset?.ticker,
      type:               d.contract_type,
      strike:             d.strike_price,
      expiration:         d.expiration_date,
      // shares_per_contract — 100 for vanilla US equity options. Anything
      // else (e.g. 47 after a 2:1 split that didn't quite cleanly halve)
      // means the contract was adjusted by OCC and DOES NOT quote via the
      // standard /v3/snapshot/options/{u}/{c} endpoint. findBestOption
      // filters these out so we never recommend a contract we can't mark.
      sharesPerContract:  d.shares_per_contract ?? null,
      // additional_underlyings — also a signal of an adjusted contract
      // (corporate action delivered cash + multiple instruments).
      isAdjusted:         !!(d.shares_per_contract && d.shares_per_contract !== 100)
                       || Array.isArray(d.additional_underlyings) && d.additional_underlyings.length > 0,
      last:               day.last_updated ? day.close : null,
      bid:                lq.bid,
      ask:                lq.ask,
      bidSize:            lq.bid_size ?? null,
      askSize:            lq.ask_size ?? null,
      midpoint:           lq.bid && lq.ask ? +((lq.bid + lq.ask) / 2).toFixed(2) : day.close,
      change:             day.change,
      changePct:          day.change_percent,
      volume:             day.volume || 0,
      openInterest:       r.open_interest || 0,
      delta:              g.delta ?? null,
      gamma:              g.gamma ?? null,
      theta:              g.theta ?? null,
      vega:               g.vega  ?? null,
      iv:                 r.implied_volatility ?? null,
    };
  });
}

// Find the best option for a directional trade.
// Filters out deep OTM and low-delta contracts so only tradeable options are
// returned, AND filters out contracts that would later fail to quote — see
// the open-interest / adjusted-contract gates below for context.
export async function findBestOption(ticker, direction, spotPrice) {
  const chain = await getOptionsChain(ticker);
  if (chain.length === 0) return null;

  const optType = direction === 'LONG' ? 'call' : 'put';
  const candidates = chain.filter(o => o.type === optType);
  if (candidates.length === 0) return null;

  const now = new Date();
  const targetStrike = direction === 'LONG' ? spotPrice * 1.02 : spotPrice * 0.98;

  // Hard limits — options outside these are misleading to show
  const MAX_STRIKE_PCT  = 0.20; // reject strikes > 20% away from spot
  const MIN_DELTA       = 0.15; // reject options with |delta| < 0.15 (too far OTM)
  const MIN_OPEN_INT    = 10;   // reject phantom/orphan contracts that nobody holds —
                                // these are the ones that 404 on /v3/snapshot/options
                                // after the chain endpoint stops serving them.
  const MAX_SPREAD_PCT  = 0.30; // bid/ask spread filter. >30% of bid means
                                // entering at ask and exiting at bid would
                                // burn nearly a third of the premium to
                                // friction — functionally untradeable.
                                // Only fires when bid/ask are actually
                                // populated (Polygon Options Developer+);
                                // no-op on Starter where lq is absent.

  let best = null;
  let bestScore = Infinity;

  for (const opt of candidates) {
    if (!opt.expiration) continue;
    const days = (new Date(opt.expiration) - now) / (1000 * 60 * 60 * 24);
    if (days < 7 || days > 60) continue;
    if (!opt.bid && !opt.last && !opt.midpoint) continue;

    // Reject strikes too far from current price
    const strikePct = Math.abs(opt.strike - spotPrice) / spotPrice;
    if (strikePct > MAX_STRIKE_PCT) continue;

    // Reject near-zero delta options (deep OTM / nearly expired)
    if (opt.delta != null && Math.abs(opt.delta) < MIN_DELTA) continue;

    // Reject adjusted contracts — they don't quote via the standard snapshot
    // endpoint, so a user holding one would see "Options contract not found"
    // every time we try to mark it.
    if (opt.isAdjusted) continue;

    // Reject contracts with no real open interest. The SMU $13 PUT issue
    // reported by users was a phantom contract Polygon's chain returned at
    // signal-creation time but the snapshot endpoint never indexed. Open
    // interest is the cleanest "real contract" signal — if 0 humans hold
    // it AND 0 traded today, we're recommending a synthetic listing that
    // we can't reliably mark.
    if ((opt.openInterest || 0) < MIN_OPEN_INT && (opt.volume || 0) === 0) continue;

    // Live bid/ask spread filter. On Polygon Options Developer (real-time
    // NBBO) we get bid + ask populated; reject anything with a spread wider
    // than MAX_SPREAD_PCT so we only recommend contracts that are actually
    // tradeable both directions. On Starter (lq absent) this is a no-op —
    // opt.bid and opt.ask are null, so the condition short-circuits.
    if (opt.bid != null && opt.ask != null && opt.bid > 0) {
      const spread = (opt.ask - opt.bid) / opt.bid;
      if (spread > MAX_SPREAD_PCT) continue;
    }

    const strikeDist = Math.abs(opt.strike - targetStrike) / spotPrice;
    const daysDist   = Math.abs(days - 21) / 30;
    const score      = strikeDist + daysDist;

    if (score < bestScore) { bestScore = score; best = opt; }
  }

  return best;
}

// ============================================================================
// OPTION CONTRACT QUOTE — live bid/ask for a single contract
// Used by TradingPage to show real current value (live bid/ask midpoint).
// ============================================================================

function parseOptionSnapshot(r) {
  if (!r) return null;
  const lq  = r.last_quote || {};
  const day = r.day        || {};
  const g   = r.greeks     || {};
  const bid = lq.bid  ?? null;
  const ask = lq.ask  ?? null;

  // ── Timestamps ──
  // Two different things that callers conflated before:
  //   quotedAt     — when Polygon's bid/ask was last updated (null if no live quote).
  //   lastTradeAt  — when the contract last actually traded (can be weeks old
  //                  for illiquid OTM contracts).
  // Returning them separately so the UI can say "no live quote, last trade
  // 28 days ago" honestly instead of relabelling one as the other.
  const nanoToMs   = (ns) => ns ? Math.floor(ns / 1_000_000) : null;
  const quotedAt     = nanoToMs(lq.last_updated ?? null);
  const lastTradeAt  = nanoToMs(r.last_trade?.sip_timestamp ?? null);
  const dayUpdatedAt = nanoToMs(day.last_updated ?? null);

  // ── Midpoint ──
  // liveMidpoint  — bid/ask actually present right now.
  // closeMidpoint — today's session close / vwap (acceptable mark for
  //                 end-of-day or recent-session valuation).
  // tradeMidpoint — last_trade.price; can be ancient. We carry it but the
  //                 caller decides whether it's trustworthy enough to use
  //                 as a "current" mark, using lastTradeAt to judge.
  const liveMidpoint = lq.midpoint
    ?? (bid != null && ask != null ? +((bid + ask) / 2).toFixed(4) : null);
  const closeMidpoint = day.close ?? day.vwap ?? day.previous_close ?? null;
  const tradeMidpoint = r.last_trade?.price ?? null;
  const midpoint = liveMidpoint ?? closeMidpoint ?? tradeMidpoint ?? null;
  if (midpoint == null) return null;

  const source = liveMidpoint != null
    ? 'live'
    : closeMidpoint != null
      ? 'session_close'
      : 'last_trade';

  return {
    midpoint, bid, ask,
    bidSize:         lq.bid_size       ?? null,
    askSize:         lq.ask_size       ?? null,
    isLive:          liveMidpoint != null,
    midpointSource:  source,           // 'live' | 'session_close' | 'last_trade'
    contractSymbol:  r.details?.ticker ?? null,
    last:            tradeMidpoint ?? day.close ?? null,
    // Polygon tags each datum it sends with a timeframe — "REAL-TIME" on
    // Advanced, "DELAYED" on Developer for trades. Surfacing it lets the UI
    // explicitly tell the user "your tier serves 15-min delayed data" instead
    // of letting them guess from a missing freshness badge.
    lastTradeTimeframe: r.last_trade?.timeframe ?? null,
    quoteTimeframe:     lq.timeframe ?? null,
    volume:          day.volume          || 0,
    openInterest:    r.open_interest     || 0,
    iv:              r.implied_volatility ?? null,
    delta:           g.delta ?? null,
    gamma:           g.gamma ?? null,
    theta:           g.theta ?? null,
    vega:            g.vega  ?? null,
    // Underlying-asset block — Developer tier populates this with the
    // current stock price (vs only the option's own data on Starter). Saves
    // us a separate /v2/snapshot call for the stock when computing P&L.
    underlyingPrice:    r.underlying_asset?.price ?? null,
    underlyingChange:   r.underlying_asset?.change_to_break_even ?? null,
    underlyingTimeframe: r.underlying_asset?.timeframe ?? null,
    quotedAt,                          // live quote time, null if no live quote
    lastTradeAt,                       // last actual trade time, can be ancient
    dayUpdatedAt,                      // when Polygon's day aggregate was last refreshed
  };
}

// By stored OCC contract symbol — e.g. "O:AAPL240119C00180000".
//
// On Polygon Options Developer, the SINGLE-contract snapshot endpoint
// (/v3/snapshot/options/{u}/{c}) does NOT return last_quote/NBBO data, but the
// CHAIN endpoint (/v3/snapshot/options/{u}) returns full NBBO for every
// contract in one payload. So we hit the chain first, find the requested
// contract by ticker, and parse it the same way. Single-contract endpoint
// stays as a fallback if the chain misses (very illiquid contract not in the
// 250-row chain, or chain endpoint errored).
//
// Net: option positions get live bid/ask on Developer instead of falling
// back to yesterday's day.close. The chain call is shared/cached for 60s
// across every position on the same underlying, so 5 open positions on the
// same stock = 1 chain fetch.
export async function getOptionContractQuote(underlyingTicker, contractSymbol, { force = false } = {}) {
  try {
    const symbol    = contractSymbol.startsWith('O:') ? contractSymbol : `O:${contractSymbol}`;
    const chainPath = `/v3/snapshot/options/${underlyingTicker}?limit=250`;

    // Primary path: chain snapshot. NBBO populated on Developer.
    try {
      const chainData = await polygonFetch(chainPath, CACHE_TTL.options, { force });
      const match = (chainData?.results || []).find(r => r.details?.ticker === symbol);
      if (match) {
        const parsed = parseOptionSnapshot(match);
        if (parsed) {
          const fetchedAt = cache.get(`poly:${chainPath}`)?.ts ?? Date.now();
          return { ...parsed, fetchedAt, _viaChain: true };
        }
      }
    } catch { /* fall through to single-contract */ }

    // Fallback path: single-contract snapshot (works on Starter for day-close,
    // also catches contracts beyond the chain's 250-row window).
    const path   = `/v3/snapshot/options/${underlyingTicker}/${symbol}`;
    const data   = await polygonFetch(path, CACHE_TTL.options, { force });
    const result = parseOptionSnapshot(data?.results);
    if (!result) return null;
    const fetchedAt = cache.get(`poly:${path}`)?.ts ?? Date.now();
    return { ...result, fetchedAt };
  } catch {
    return null;
  }
}

// By trade details (for existing trades that predate the contract_symbol column)
// type: "CALL" or "PUT", strike: number, expiry: "YYYY-MM-DD"
export async function getOptionQuoteByDetails(ticker, type, strike, expiry, { force = false } = {}) {
  try {
    const contractType = type.toLowerCase();
    const path  = `/v3/snapshot/options/${ticker}?contract_type=${contractType}&strike_price=${strike}&expiration_date=${expiry}&limit=10`;
    const data  = await polygonFetch(path, CACHE_TTL.options, { force });
    const results = data?.results;
    if (!results || results.length === 0) return null;
    // Require exact match on strike + expiry — never proxy with a different contract
    const r = results.find(r => {
      const d = r.details || {};
      return Math.abs((d.strike_price || 0) - strike) < 0.01 && d.expiration_date === expiry;
    });
    if (!r) return null;
    const result = parseOptionSnapshot(r);
    if (!result) return null;
    const fetchedAt = cache.get(`poly:${path}`)?.ts ?? Date.now();
    return { ...result, fetchedAt };
  } catch {
    return null;
  }
}

// ============================================================================
// VIX — macro volatility regime gauge
// Polygon serves VIX under the indices universe as ticker "I:VIX".
// We pull the most recent daily close — Options Starter doesn't include
// real-time index quotes, and EOD is plenty for a three-band regime gate.
// 15-min cache; the underlying datum only refreshes daily anyway.
// ============================================================================
export async function getVixLevel() {
  try {
    const data = await polygonFetch(`/v2/aggs/ticker/I:VIX/prev?adjusted=true`, 15 * 60 * 1000);
    return data?.results?.[0]?.c ?? null;
  } catch (e) {
    console.warn('[Polygon] VIX fetch failed:', e?.message || e);
    return null;
  }
}

// ============================================================================
// MARKET SCAN — grouped daily bars for ALL US-listed stocks (one API call)
// Returns ~8,000-10,000 tickers filtered down to liquid, priced candidates.
// Used by the signal engine to scan the full NYSE/NASDAQ universe instead of
// a hand-picked list.
// ============================================================================
// MARKET SCAN — full US universe via Polygon grouped daily
//
// Walks backwards from today until we find a date with data. The naive
// approach of "go back 1 day on Tuesday, 3 on Monday" breaks on every US
// market holiday — the day after Memorial Day (Tue) asks for Mon's bars,
// Polygon returns empty, the scan throws, and the signal page falls back
// to its curated list. Walking up to MAX_LOOKBACK days catches every
// holiday automatically without us maintaining a calendar.
// ============================================================================
const MAX_LOOKBACK = 7;

export async function getMarketScan({ minPrice = 5, minVolume = 500_000 } = {}) {
  const today = new Date();
  let lastErr = null;

  for (let back = 1; back <= MAX_LOOKBACK; back++) {
    const d = new Date(today);
    d.setUTCDate(d.getUTCDate() - back);
    const dow = d.getUTCDay(); // 0=Sun, 6=Sat
    if (dow === 0 || dow === 6) continue; // skip weekends — Polygon never has data

    const dateStr = d.toISOString().split('T')[0];
    try {
      const data = await polygonFetch(
        `/v2/aggs/grouped/locale/us/market/stocks/${dateStr}?adjusted=true`,
        15 * 60 * 1000
      );
      const results = data?.results;
      if (!results || results.length === 0) {
        // Holiday — Polygon returns 200 with an empty array. Try the day before.
        continue;
      }
      // Two views of the universe live on the returned array:
      //   .length    — only the names that passed the liquidity floor
      //                (price ≥ minPrice AND volume ≥ minVolume AND open > 0).
      //                This is what gets ranked + scored downstream.
      //   .rawCount  — every row Polygon returned for the day, including the
      //                penny stocks, halted names, and zero-volume listings
      //                we filtered out. Used by the Signals-page footer so
      //                users can see "we scanned 8,754 → kept 3,043 liquid"
      //                rather than the misleading "scanned 3,043".
      const filtered = results
        .filter(r => r.c >= minPrice && r.v >= minVolume && r.o > 0)
        .map(r => ({
          ticker:       r.T,
          price:        r.c,
          open:         r.o,
          high:         r.h,
          low:          r.l,
          volume:       r.v,
          vwap:         r.vw,
          change:       +(r.c - r.o).toFixed(4),
          changePct:    +((r.c - r.o) / r.o * 100).toFixed(4),
          transactions: r.n,
          _source:      'polygon',
          _fetchedAt:   Date.now(),
          _scanDate:    dateStr,
        }));
      filtered.rawCount = results.length;
      filtered.scanDate = dateStr;
      return filtered;
    } catch (e) {
      // Network / proxy hiccup — try the previous day rather than blowing up
      // and falling all the way back to the curated list.
      lastErr = e;
    }
  }
  throw new Error(`No market scan data in last ${MAX_LOOKBACK} days: ${lastErr?.message || 'unknown'}`);
}

// ── Universe-wide baselines via stacked grouped-daily snapshots ──────────────
// The grouped-daily endpoint returns OHLCV for EVERY US stock in ONE call, so
// fetching N prior trading days costs N cached calls total (not N×8000) and
// yields two per-ticker baselines the candidate-ranking needs:
//
//   • atr / atrPct — multi-day Average True Range (Wilder), the VOLATILITY
//     baseline used to normalize a day's move ("how many ATRs did this stock
//     move today" vs raw % which over-weights always-volatile names).
//   • avgVolume — mean daily volume over the window, the PARTICIPATION
//     baseline used to compute relative volume (RVOL = today ÷ avg): catches
//     unusual institutional activity even when price barely moved.
//
// Returns a Map: ticker → { atr, atrPct, avgVolume }.
// Tickers with fewer than MIN_TR_SAMPLES usable true-range samples are
// omitted (caller falls back to raw move / no-RVOL for those).
//
// True range per day t = max(
//   high_t − low_t,
//   |high_t − close_{t-1}|,
//   |low_t  − close_{t-1}|
// ) — the standard Wilder definition; the prevClose terms capture gaps that
// a plain high−low misses.
const ATR_DAYS         = 10;   // ~2 trading weeks — responsive without being noisy
const MIN_TR_SAMPLES   = 5;    // need at least a week of ranges to trust the ATR

export async function getUniverseBaselines({ days = ATR_DAYS, beforeDateStr = null } = {}) {
  // Walk back from `beforeDateStr` (the scan day) collecting `days` prior
  // trading days of grouped-daily. We over-fetch the lookback window to
  // absorb weekends/holidays.
  const anchor = beforeDateStr ? new Date(beforeDateStr + 'T00:00:00Z') : new Date();
  const perTicker = new Map();   // ticker → [{ date, h, l, c, v }] oldest→newest
  let collected = 0;

  for (let back = 1; back <= days * 2 + 5 && collected < days; back++) {
    const d = new Date(anchor);
    d.setUTCDate(d.getUTCDate() - back);
    const dow = d.getUTCDay();
    if (dow === 0 || dow === 6) continue;
    const dateStr = d.toISOString().split('T')[0];
    let data;
    try {
      data = await polygonFetch(
        `/v2/aggs/grouped/locale/us/market/stocks/${dateStr}?adjusted=true`,
        60 * 60 * 1000   // 1h cache — historical days never change
      );
    } catch {
      continue;          // skip a failed day, keep collecting
    }
    const results = data?.results;
    if (!results || results.length === 0) continue;   // holiday
    for (const r of results) {
      if (!r.T || !(r.h > 0) || !(r.l > 0) || !(r.c > 0)) continue;
      let arr = perTicker.get(r.T);
      if (!arr) { arr = []; perTicker.set(r.T, arr); }
      arr.push({ date: dateStr, h: r.h, l: r.l, c: r.c, v: r.v || 0 });
    }
    collected++;
  }

  const out = new Map();
  for (const [ticker, bars] of perTicker) {
    if (bars.length < MIN_TR_SAMPLES + 1) continue;   // need N+1 closes for N TRs
    // bars are newest→oldest (we walked backwards); sort oldest→newest so
    // prevClose is well-defined.
    bars.sort((a, b) => (a.date < b.date ? -1 : 1));
    const trs = [];
    for (let i = 1; i < bars.length; i++) {
      const prevClose = bars[i - 1].c;
      const { h, l } = bars[i];
      trs.push(Math.max(h - l, Math.abs(h - prevClose), Math.abs(l - prevClose)));
    }
    if (trs.length < MIN_TR_SAMPLES) continue;
    const atr = trs.reduce((s, x) => s + x, 0) / trs.length;
    const latestClose = bars[bars.length - 1].c;
    if (!(atr > 0) || !(latestClose > 0)) continue;
    // Average volume over the window (all bars, including the oldest — volume
    // needs no prevClose). Zero-volume halted days are already excluded above.
    const vols = bars.map(b => b.v).filter(v => v > 0);
    const avgVolume = vols.length > 0 ? Math.round(vols.reduce((s, v) => s + v, 0) / vols.length) : 0;
    out.set(ticker, {
      atr:       +atr.toFixed(4),
      atrPct:    +((atr / latestClose) * 100).toFixed(4),
      avgVolume,
    });
  }
  return out;
}

// ============================================================================
// TECHNICAL INDICATORS — computed locally from Polygon bars
//
// The per-ticker Polygon /v1/indicators/* RSI/MACD/EMA helpers were removed in
// T0.4 (they had no caller). Every indicator is now derived from daily + weekly
// bars by the server's /api/market/technicals route in one round trip.
// ============================================================================

// Full technicals — RSI, MACD, Bollinger, Stochastic, ADX, OBV, SuperTrend,
// EMA50, weekly RSI, weekly MACD — for a batch of tickers in ONE HTTP call.
//
// Backed by /api/market/technicals on the server, which computes everything
// locally from Polygon daily + weekly bars: 1 client call → server fetches
// 2 bar series per ticker → all indicators returned in a single response.
//
// The server route caps at 100 tickers per request to bound its own work; we
// chunk client-side so any pool size works transparently. Chunks fire in
// parallel and merge — net latency stays at ~1 round trip even with 600+
// tickers. Chunk failures are isolated: a 500 on one batch returns nulls for
// those tickers without poisoning the others.
const BATCH_TECHNICALS_CHUNK_SIZE = 90;   // server cap is 100; leave headroom

export async function getBatchTechnicals(tickers) {
  if (!tickers || tickers.length === 0) return {};

  const chunks = [];
  for (let i = 0; i < tickers.length; i += BATCH_TECHNICALS_CHUNK_SIZE) {
    chunks.push(tickers.slice(i, i + BATCH_TECHNICALS_CHUNK_SIZE));
  }

  const results = await Promise.all(chunks.map(async (chunk) => {
    try {
      const url = `${API_BASE}/market/technicals?tickers=${encodeURIComponent(chunk.join(','))}`;
      const res = await fetch(url, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error(`technicals HTTP ${res.status}`);
      return await res.json();
    } catch (e) {
      console.warn(`[Polygon] getBatchTechnicals chunk (${chunk.length} tickers) failed:`, e?.message || e);
      // Null-fill the failed chunk so downstream gates skip cleanly; never
      // poisons the other chunks' results.
      return Object.fromEntries(chunk.map(t => [t, null]));
    }
  }));

  return Object.assign({}, ...results);
}

// ============================================================================
// SECTOR LOOKUP
// ============================================================================
// Batched ticker → sector classification via the server's /api/market/sectors
// endpoint (which proxies Polygon's /v3/reference/tickers SIC code). Used by
// the signal engine to gate LONG candidates out of lagging sectors and SHORT
// candidates out of leading sectors. 24h cache on the server side means a
// signal-page refresh costs effectively zero after the first call per ticker
// per day.
// Server caps at 200 tickers; chunk client-side so a 600-candidate scan
// transparently fans out across 3 parallel requests. Per-chunk failure
// degrades to 'other' for that chunk without poisoning the rest.
const BATCH_SECTORS_CHUNK_SIZE = 180;   // server cap is 200; leave headroom

export async function getSectorMap(tickers) {
  if (!tickers || tickers.length === 0) return {};

  const chunks = [];
  for (let i = 0; i < tickers.length; i += BATCH_SECTORS_CHUNK_SIZE) {
    chunks.push(tickers.slice(i, i + BATCH_SECTORS_CHUNK_SIZE));
  }

  const results = await Promise.all(chunks.map(async (chunk) => {
    try {
      const url = `${API_BASE}/market/sectors?tickers=${encodeURIComponent(chunk.join(','))}`;
      const res = await fetch(url, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error(`sectors HTTP ${res.status}`);
      return await res.json();
    } catch (e) {
      console.warn(`[Polygon] getSectorMap chunk (${chunk.length} tickers) failed:`, e?.message || e);
      return Object.fromEntries(chunk.map(t => [t, 'other']));
    }
  }));

  return Object.assign({}, ...results);
}

// ============================================================================
// SYMBOL SEARCH
// ============================================================================
export async function searchSymbols(query) {
  const q = query.trim().toUpperCase();

  // Run exact ticker lookup + fuzzy search in parallel.
  // Exact lookup ensures e.g. "META" always surfaces Meta Platforms
  // even when fuzzy search ranks other results first.
  const [fuzzyData, exactData] = await Promise.all([
    polygonFetch(
      `/v3/reference/tickers?search=${encodeURIComponent(query)}&market=stocks&active=true&limit=10`,
      CACHE_TTL.search
    ).catch(() => null),
    polygonFetch(
      `/v3/reference/tickers?ticker=${encodeURIComponent(q)}&market=stocks&active=true&limit=1`,
      CACHE_TTL.search
    ).catch(() => null),
  ]);

  const seen = new Set();
  const merged = [];

  // Exact match goes first
  for (const s of exactData?.results || []) {
    if (!seen.has(s.ticker)) { seen.add(s.ticker); merged.push(s); }
  }
  // Fuzzy results fill the rest
  for (const s of fuzzyData?.results || []) {
    if (!seen.has(s.ticker)) { seen.add(s.ticker); merged.push(s); }
  }

  return merged.slice(0, 10).map(s => ({
    ticker:   s.ticker,
    name:     s.name,
    type:     s.type,
    market:   s.market,
    exchange: s.primary_exchange,
  }));
}

// ============================================================================
// COMPANY PROFILE
// ============================================================================
export async function getCompanyProfile(ticker) {
  const data = await polygonFetch(
    `/v3/reference/tickers/${ticker}`,
    24 * 60 * 60 * 1000  // essentially static
  );

  const r = data?.results;
  if (!r) throw new Error(`No profile for ${ticker}`);

  return {
    ticker:    r.ticker,
    name:      r.name,
    exchange:  r.primary_exchange,
    industry:  r.sic_description,
    logo:      r.branding?.logo_url,
    marketCap: r.market_cap,
    weburl:    r.homepage_url,
    country:   r.locale === 'us' ? 'US' : r.locale,
    currency:  r.currency_name,
    phone:     r.phone_number,
  };
}

// ============================================================================
// NEWS
// Polygon returns pre-computed LLM sentiment per ticker in insights[].
// ============================================================================
export async function getCompanyNews(ticker, daysBack = 3) {
  const from = new Date(Date.now() - daysBack * 86400000).toISOString().split('T')[0];
  const data = await polygonFetch(
    `/v2/reference/news?ticker=${ticker}&published_utc.gte=${from}&limit=10&sort=published_utc&order=desc`,
    5 * 60 * 1000
  );

  return (data?.results || []).map(n => ({
    headline: n.title,
    summary:  n.description || '',
    source:   n.publisher?.name || '',
    url:      n.article_url,
    image:    n.image_url,
    datetime: new Date(n.published_utc).getTime() / 1000,
    // Pre-computed sentiment from Polygon's LLM, scoped to this ticker
    sentiment: n.insights?.find(i => i.ticker === ticker)?.sentiment || 'neutral',
  }));
}

// ============================================================================
// EARNINGS CALENDAR — Benzinga-powered endpoint on Polygon. Returns upcoming
// and recent earnings within a date window. Returns null if the subscription
// tier doesn't include Benzinga data, so the caller can fall back gracefully.
// ============================================================================
export async function getEarningsCalendar({ daysBack = 2, daysForward = 5 } = {}) {
  const today = new Date();
  const from  = new Date(today.getTime() - daysBack    * 86400000).toISOString().split('T')[0];
  const to    = new Date(today.getTime() + daysForward * 86400000).toISOString().split('T')[0];

  try {
    const data = await polygonFetch(
      `/benzinga/v1/earnings?date.gte=${from}&date.lte=${to}&limit=1000`,
      30 * 60 * 1000  // 30 min — calendar updates ~once a day
    );
    if (!data?.results) return null;
    const map = {};
    for (const e of data.results) {
      const ticker = (e.ticker || '').toUpperCase();
      if (ticker && e.date) map[ticker] = e.date;
    }
    return map;
  } catch {
    return null;
  }
}

export async function getMarketNews() {
  const data = await polygonFetch(
    `/v2/reference/news?limit=20&sort=published_utc&order=desc`,
    5 * 60 * 1000
  );

  return (data?.results || []).map(n => ({
    id:       n.id,
    headline: n.title,
    summary:  n.description || '',
    source:   n.publisher?.name || '',
    url:      n.article_url,
    image:    n.image_url,
    datetime: new Date(n.published_utc).getTime() / 1000,
    related:  n.tickers?.join(',') || '',
    category: n.keywords?.[0] || 'general',
  }));
}
